import type {
  AlertRuleCommonParams,
  AlertRuleIntervalType,
  CompRiskLevel,
  ListsCountriesCodes,
} from '@/api';

type CommonFormParams = {
  minVolume?: string;
  countries?: string[];
  isSuspended?: boolean;
  isBlacklisted?: boolean;
  targetCustomerRisk?: CompRiskLevel;
  inactivityType?: AlertRuleIntervalType;
  inactivityValue?: string;
  intervalType?: AlertRuleIntervalType;
  intervalValue?: string;
};

export function buildAlertRuleCommonParams(
  p: CommonFormParams,
): AlertRuleCommonParams {
  const minVolume =
    p.minVolume && Number(p.minVolume) > 0 ? Number(p.minVolume) : undefined;
  const countries =
    p.countries?.length && p.countries.some((c) => c?.trim())
      ? p.countries.map((c) => c.trim()).filter(Boolean)
      : undefined;
  const isSuspended = p.isSuspended != null ? p.isSuspended : undefined;
  const isBlacklisted = p.isBlacklisted != null ? p.isBlacklisted : undefined;
  const targetCustomerRisk = p.targetCustomerRisk ?? undefined;

  const hasInactivity =
    p.inactivityType && p.inactivityValue && Number(p.inactivityValue) > 0;
  const hasInterval =
    p.intervalType && p.intervalValue && Number(p.intervalValue) > 0;

  return {
    ...(minVolume != null && { minVolume }),
    ...(countries != null &&
      countries.length > 0 && {
        countries: countries as ListsCountriesCodes[],
      }),
    ...(isSuspended != null && { isSuspended }),
    ...(isBlacklisted != null && { isBlacklisted }),
    ...(targetCustomerRisk != null && { targetCustomerRisk }),
    ...(hasInactivity && {
      inactivityType: p.inactivityType,
      inactivityValue: Number(p.inactivityValue),
    }),
    ...(hasInterval && {
      intervalType: p.intervalType,
      intervalValue: Number(p.intervalValue),
    }),
  };
}

function isEmptyOrZero(value: string): boolean {
  return value === '' || Number(value) === 0;
}

export function validateMinAmount(value: string, maxAmount: string): boolean {
  const minEmpty = isEmptyOrZero(value);
  const maxEmpty = maxAmount === '';

  if (minEmpty && maxEmpty) return false;

  if (minEmpty) return true;

  if (Number(value) < 0) return false;

  if (!maxEmpty && Number(maxAmount) > 0) {
    return Number(value) < Number(maxAmount);
  }

  return true;
}

export function validateMaxAmount(value: string, minAmount: string): boolean {
  const maxEmpty = value === '';
  const minEmpty = isEmptyOrZero(minAmount);

  if (maxEmpty && minEmpty) return false;

  if (maxEmpty) return true;

  if (Number(value) <= 0) return false;

  const minVal = minEmpty ? 0 : Number(minAmount);
  return Number(value) > minVal;
}
